/**
 * DP CLAIM LAB — DP101 chapter 1. What a DP claim must NAME before it is a claim.
 *
 * ── THE CHAPTER'S SHARPEST SENTENCE IS A TEST, SO IT IS GIVEN AS ONE ──────────────────────────
 * §6: a real specification "names an environment, a heading and a power state, and stops", and a
 * claim without that shape — "DP2, holds position" — "is not a claim about a vessel at all". That
 * is a three-part check a learner can run on any brochure he is ever handed, which makes it the one
 * thing in this chapter worth practising rather than reading.
 *
 * ── THE INCOMPLETE CLAIMS ARE REAL ONES ───────────────────────────────────────────────────────
 * A claim invented to be obviously empty teaches spotting my writing. "DP2, holds position" is on
 * real brochures, and the graded sequence here is the one an enquiry actually walks: a bare
 * notation, then a wind speed, then a heading, and only at the end the power state that makes it
 * a specification. The complete one is HER declared criterion, read off the model rather than
 * retyped — the vessel is the worked example.
 */
import { useState, useEffect } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused, absent } from '../../../packages/ankr-provenance/src/provenance.mjs';
import {
  CLAIMS_CH1, CLAIM_PARTS, CLAIM_SHAPE, BEAM_IS_THE_SPEC, DP_DEFINITION,
  SOURCE_CH1, criterionOf, WHY_NOT_SCORED,
} from './dplabs.mjs';

export default function DpClaimLab() {
  const [open, setOpen] = useState({});
  const [model, setModel] = useState(null);
  useEffect(() => { let live = true; fetch('/api/dp/model').then((r) => r.json()).then((m) => live && setModel(m)).catch(() => {}); return () => { live = false; }; }, []);
  const crit = model ? criterionOf(model) : null;

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Claim bench · is this a claim about a vessel?</div>
      <h2 className="g-h2 lab-h">“DP2, holds position” names nothing.</h2>

      <p className="g-body">
        <Value of={cited(CLAIM_SHAPE, SOURCE_CH1)} />
      </p>
      <p className="g-body g-dim">
        A dynamically positioned vessel maintains her position <Value of={cited(DP_DEFINITION, SOURCE_CH1)} />.
        Not mainly. Not assisted by. If a mooring line is holding her, she is moored.
      </p>

      <div className="lab-task">
        <div className="g-label">Three parts. A claim missing any of them is not yet a claim.</div>
        <div className="dpc-parts">
          {CLAIM_PARTS.map((p) => (
            <div key={p.id} className="dpc-part"><b>{p.name}</b><span className="g-dim"> — {p.how}</span></div>
          ))}
        </div>
      </div>

      <div className="lab-task">
        <div className="g-label">Which parts does each of these name? Open one to check yourself.</div>
        {CLAIMS_CH1.map((c) => {
          const shown = open[c.id];
          const missing = CLAIM_PARTS.filter((p) => !c.has.includes(p.id));
          return (
            <div key={c.id} className={`dpc-claim ${shown ? (missing.length ? 'clm-miss' : 'clm-ok') : ''}`}>
              <p className="g-body clm-text">“{c.text}”</p>
              {!shown ? (
                <button className="g-btn" onClick={() => setOpen((o) => ({ ...o, [c.id]: true }))}>what does it name?</button>
              ) : (
                <>
                  <p className="g-body">
                    {CLAIM_PARTS.map((p) => (
                      <span key={p.id} className="dpc-chip">
                        {c.has.includes(p.id)
                          ? <Value of={computed(p.name)} />
                          : <Value of={absent()} />}{' '}
                        <span className="g-dim">{p.name}</span>{' '}
                      </span>
                    ))}
                  </p>
                  <p className="g-body">
                    {missing.length === 0
                      ? <Value of={computed('All three. This is a specification.')} />
                      : <Value of={refused(`Names ${c.has.length} of 3. Missing: ${missing.map((p) => p.name).join(', ')}.`)} />}
                  </p>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="lab-task dpb-verdict">
        <div className="g-label">Her own criterion, read off her declaration</div>
        {crit ? (
          <>
            <p className="g-body">
              <Value of={computed(`${crit.wind_kn} kn wind · sea state ${crit.sea_state} (Hs ${crit.Hs_m} m) · ${crit.current_kn} kn current · ${crit.direction}`)} />
            </p>
            <p className="g-body g-dim">{crit.basis}</p>
            <p className="g-body"><Value of={cited(BEAM_IS_THE_SPEC, SOURCE_CH1)} /></p>
          </>
        ) : (
          <p className="g-body"><Value of={absent()} /> Her declared criterion is not available in this session, so it is withheld rather than approximated.</p>
        )}
      </div>

      <p className="g-body g-dim lab-foot">{WHY_NOT_SCORED}</p>
      <p className="g-body g-dim"><Value of={absent()} /> Nothing here is an attestation.</p>
    </div>
  );
}
